import { useState, useEffect } from "react";
import {
  Autocomplete,
  DatePicker,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
} from "../../../../components";
import {
  PessoaService,
  MeioPagamentoService,
  BancosCaixasService,
  SessionService,
  RotaService,
} from "../../../../services";
import { responseErros } from "../../../../utils";
import {
  listaAgrupadoresContasReceber,
  listaStatus,
  listaTiposContasReceber,
} from "./entities/constantes";
import { PermisoesHelper, value } from "../../../../helpers";
import { useFiltrosRelatoriosHandlers } from "../../components/use-filtros-relatorios-handlers";

const pessoaService = new PessoaService();
const bancosCaixasService = new BancosCaixasService();
const meioPagamentoService = new MeioPagamentoService();
const rotasService = new RotaService();
const sessionService = new SessionService();
const permissoesHelper = new PermisoesHelper();

const FormFiltroAvancadoContasReceber = ({
  filtrosAvancados,
  setFiltrosAvancados,
  setFiltrosAplicadosImpressao,
  setValidationError,
}) => {
  const [clienteList, setClienteList] = useState([]);
  const [vendedorList, setVendedorList] = useState([]);
  const [contasList, setContasList] = useState([]);
  const [rotaList, setRotaList] = useState([]);
  const { usuario, empresa } = sessionService.getEmpresaAndUsuario();
  const [meioPagamentoList, setMeioPagamentoList] = useState([]);
  const [listaStatusFiltrado, setListaStatusFiltrado] = useState(listaStatus);
  const {
    onChangeAutocomplete,
    onChangeDate,
    onChangeInput,
    onChangeCheckbox,
  } = useFiltrosRelatoriosHandlers(
    setFiltrosAvancados,
    setFiltrosAplicadosImpressao,
  );

  useEffect(() => {
    setFiltrosAvancados({
      ...filtrosAvancados,
      tipo: "TITULO_NORMAL",
      grupoId: 0,
    });
  }, []);

  function resetarFiltrosDeStatus() {
    setFiltrosAvancados((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp.status;
      return prevStateTemp;
    });
    setFiltrosAplicadosImpressao((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp["STATUS"];
      return prevStateTemp;
    });
    setListaStatusFiltrado(listaStatus);
  }

  const isStatusParcial = (status) =>
    status.value !== "BAIXADO_PARCIAL" &&
    (filtrosAvancados.status?.length || 0) <= 1 &&
    (status.value === "ABERTO" || status.value === "BAIXADO");

  const onChangeMultipleStatus = (valores = []) => {
    if (!valores?.length) {
      resetarFiltrosDeStatus();
      return;
    }
    const statusList = valores.map(({ value }) => value);
    const addValorParcial = valores.every((status) => isStatusParcial(status));
    const updatedValores = addValorParcial
      ? [...statusList, "BAIXADO_PARCIAL"]
      : statusList;
    updateFilters(updatedValores);
    updateListaStatus(updatedValores);
  };

  const formatarString = (texto) => {
    const split = texto.split("_");
    const formatStatus = split.map(
      (p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
    );
    return formatStatus.join(" ");
  };

  const updateFilters = (valores) => {
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      status: valores,
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      Status: valores.map(formatarString),
    }));
  };

  const updateListaStatus = (valores) => {
    const listaTemp = listaStatus.filter(
      (status) => !valores.some((value) => value === status.value),
    );
    setListaStatusFiltrado(listaTemp);
  };

  useEffect(() => {
    buscarClientes();
    buscarVendedores();
    buscarRotas();
    buscarMeiosPagamento();
    buscarContas();
    setEstadoInicial();
  }, []);

  function setEstadoInicial() {
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      empresasIds: [empresa.id],
    }));

    if (usuario?.acessosEmpresas?.length) {
      setFiltrosAplicadosImpressao((prevState) => ({
        ...prevState,
        ["Empresas-Associadas"]: empresa.razaoSocial,
      }));
    }
  }

  const buscarClientes = () => {
    const filtros = {
      nonPaginated: true,
      tiposPessoaId: 1,
    };
    pessoaService.getAll(filtros).then((result) => {
      if (!result.isAxiosError) {
        setClienteList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarVendedores = () => {
    const filtro = {
      nonPaginated: true,
      vendedor: true,
      tiposPessoaId: 4,
    };
    pessoaService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setVendedorList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarRotas = () => {
    const filtro = {
      nonPaginated: true,
    };
    rotasService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setRotaList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarMeiosPagamento = () => {
    meioPagamentoService.getAll({ ativado: true }).then((result) => {
      if (!result.isAxiosError) {
        setMeioPagamentoList(result.data.rows);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarContas = () => {
    const filtro = {
      nonPaginated: true,
    };
    bancosCaixasService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setContasList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  function onChangeMultipleEmpresas(empresas) {
    if (empresas?.length === 0) {
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      empresasIds: empresas.map(({ id }) => id),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      "Empresas-Associadas": empresas
        .map(({ razaoSocial }) => razaoSocial)
        .join(" | "),
    }));
  }

  return (
    <>
      {usuario?.acessosEmpresas?.length > 0 && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="empresasIds"
                name="empresasIds"
                options={usuario.acessosEmpresas}
                noOptionsText="Sem opções"
                autoHighlight
                disablePortal
                filterSelectedOptions
                getOptionLabel={(option) => option?.razaoSocial || ""}
                onChange={(_, newValue) => onChangeMultipleEmpresas(newValue)}
                value={value.multiple(
                  usuario.acessosEmpresas || [],
                  filtrosAvancados["empresasIds"]
                    ? filtrosAvancados["empresasIds"]
                    : "",
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    required
                    label="Empresas"
                    margin="normal"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
        </>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="grupoId"
              name="grupoId"
              options={listaAgrupadoresContasReceber}
              noOptionsText="Sem opções"
              autoHighlight
              disablePortal
              disableClearable
              getOptionLabel={(option) => option.label ?? ""}
              onChange={(_, newValue) =>
                onChangeAutocomplete("grupoId", newValue, "Agrupado-Por")
              }
              value={value.autoComplete(
                listaAgrupadoresContasReceber,
                filtrosAvancados?.grupoId,
              )}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Agrupar Por" />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Autocomplete
            id="pagadorId"
            name="Pagador"
            options={clienteList}
            noOptionsText="Sem opções"
            autoHighlight
            disablePortal
            margin="normal"
            getOptionLabel={(option) =>
              option.nomeRazaoSocial ? option.nomeRazaoSocial : ""
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pagador"
                variant="outlined"
                margin="normal"
              />
            )}
            onChange={(e, value) =>
              onChangeAutocomplete("pagadorId", value, "Pagador")
            }
            value={value.autoComplete(clienteList, filtrosAvancados?.pagadorId)}
          />
        </Grid>
        <Grid item xs={6}>
          <Select
            label="Tipo"
            id="tipo"
            name="tipo"
            margin="normal"
            variant="outlined"
            value={value.text(filtrosAvancados?.tipo)}
            onChange={(event) => onChangeInput(event, "Tipo")}
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: {
                  sx: {
                    zIndex: 1600,
                  },
                },
              },
            }}
          >
            {listaTiposContasReceber.map((tipo, index) => (
              <MenuItem key={index} value={tipo.value}>
                {tipo.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Autocomplete
            id="vendedorId"
            name="Vendedor"
            options={vendedorList}
            noOptionsText="Sem opções"
            autoHighlight
            disablePortal
            margin="normal"
            getOptionLabel={(option) =>
              option.nomeRazaoSocial ? option.nomeRazaoSocial : ""
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vendedor"
                variant="outlined"
                margin="normal"
              />
            )}
            onChange={(e, value) =>
              onChangeAutocomplete("vendedorId", value, "Vendedor")
            }
            value={value.autoComplete(
              vendedorList,
              filtrosAvancados?.vendedorId,
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            id="rotaId"
            name="Rota"
            options={rotaList}
            noOptionsText="Sem opções"
            autoHighlight
            disablePortal
            margin="normal"
            getOptionLabel={(option) =>
              option.descricao ? option.descricao : ""
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Rota"
                variant="outlined"
                margin="normal"
              />
            )}
            onChange={(e, value) =>
              onChangeAutocomplete("rotaId", value, "Rota")
            }
            value={value.autoComplete(rotaList, filtrosAvancados?.rotaId)}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="meioPagamentoId"
              name="Meio-de-Pagamento"
              options={meioPagamentoList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) =>
                option.id && option.descricao
                  ? `${option.id} - ${option.descricao}`
                  : ""
              }
              getOptionSelected={(option, value) =>
                option?.descricao === value?.descricao
              }
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Meios de pagamento"
                  variant="outlined"
                />
              )}
              onChange={(e, value) =>
                onChangeAutocomplete(
                  "meioPagamentoId",
                  value,
                  "Meio-de-Pagamento",
                )
              }
              value={value.autoComplete(
                meioPagamentoList,
                filtrosAvancados?.meioPagamentoId,
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="finBancoCaixaId"
              name="Conta"
              options={contasList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option.nome ?? ""}
              getOptionSelected={(option, value) =>
                option?.nome === value?.nome
              }
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField {...params} label="Conta" variant="outlined" />
              )}
              onChange={(e, value) =>
                onChangeAutocomplete("finBancoCaixaId", value, "Conta")
              }
              value={value.autoComplete(
                contasList,
                filtrosAvancados?.finBancoCaixaId,
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataLancamentoStart"
            name="Data-Lançamento-De"
            label="Data Lançamento De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataLancamentoStart)}
            onChange={(date) =>
              onChangeDate("dataLancamentoStart", date, "Data-Lançamento-De")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataLancamentoEnd"
            name="Data-Lançamento-Até"
            label="Data Lançamento Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataLancamentoEnd)}
            onChange={(date) =>
              onChangeDate("dataLancamentoEnd", date, "Data-Lançamento-Até")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataVencimentoStart"
            name="Data-Vencimento-De"
            label="Data Vencimento De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataVencimentoStart)}
            onChange={(date) =>
              onChangeDate("dataVencimentoStart", date, "Data-Vencimento-De")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataVencimentoEnd"
            name="Data-Vencimento-Até"
            label="Data Vencimento Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataVencimentoEnd)}
            onChange={(date) =>
              onChangeDate("dataVencimentoEnd", date, "Data-Vencimento-Até")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataPagamentoStart"
            name="Data-Pagamento-De"
            label="Data Pagamento De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataPagamentoStart)}
            onChange={(date) =>
              onChangeDate("dataPagamentoStart", date, "Data-Pagamento-De")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataPagamentoEnd"
            name="Data-Pagamento-Até"
            label="Data Pagamento Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataPagamentoEnd)}
            onChange={(date) =>
              onChangeDate("dataPagamentoEnd", date, "Data-Pagamento-Até")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>
      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Select
            id="ordenarPor"
            name="ordenarPor"
            margin="normal"
            value={value.text(filtrosAvancados?.ordenarPor)}
            label="Ordenar Por"
            onChange={(event) => onChangeInput(event, "ordenarPor")}
            fullWidth
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: {
                  sx: {
                    zIndex: 1600,
                  },
                },
              },
            }}
          >
            <MenuItem value={"DATA_VENCIMENTO"}>Data Vencimento</MenuItem>
            <MenuItem value={"DATA_LANCAMENTO"}>Data Lançamento</MenuItem>
            <MenuItem value={"DATA_PAGAMENTO"}>Data Pagamento</MenuItem>
          </Select>
        </Grid>
      </Grid>
      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              multiple
              id="status"
              name="status"
              options={listaStatusFiltrado}
              noOptionsText="Sem opções"
              autoHighlight
              disablePortal
              getOptionLabel={(option) => {
                return (
                  <div>
                    <i
                      className="ph-fill ph-circle"
                      style={{ color: `${option.cor}`, marginRight: "10px" }}
                    ></i>
                    {option.label}
                  </div>
                );
              }}
              onChange={(_, newValue) => onChangeMultipleStatus(newValue)}
              value={
                filtrosAvancados?.status
                  ? listaStatus.filter((status) =>
                      filtrosAvancados.status.some(
                        (value) => value === status.value,
                      ),
                    )
                  : []
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  required
                  label="Status"
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      {permissoesHelper.temPermisao(
        "relatorio-historico-pagamentos-contas-receber",
      ) && (
        <Grid container spacing={2}>
          <Grid item xs={12} alignContent="center" alignItems="center">
            <FormControlLabel
              label="Exibir Histórico de Pagamentos"
              control={
                <Checkbox
                  id="historicoPagamentos"
                  name="historicoPagamentos"
                  checked={filtrosAvancados?.historicoPagamentos || false}
                  onChange={onChangeCheckbox}
                  color="primary"
                />
              }
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default FormFiltroAvancadoContasReceber;
